/**
 * Public self-service sign-up (/signup).
 *
 * Anonymous, es-ES, NO password field: the identity is provider-owned. A short
 * two-step flow — pick a plan, then enter details — whose ending depends on how
 * the instance takes on clients:
 *
 * - **linked**: the account is created (with a pending plan) on submit and the
 *   browser is handed to the payment platform. Nothing is usable until payment
 *   confirms server-side.
 * - **manual**: nothing is charged online and NO account is created. The visitor
 *   leaves a request that a person reads, and the flow ends on a confirmation
 *   screen.
 *
 * The mode is read before anything is shown, together with the plans, so the
 * visitor never sees a funnel whose behaviour changes underneath them.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  MarkEmailReadOutlined,
  RadioButtonUncheckedRounded,
} from '@mui/icons-material';
import PublicShell from './PublicShell';
import { goToSignIn } from './signIn';
import { HttpError } from '@/utils/httpClient';
import {
  fetchPublicConfig,
  fetchPublicPlans,
  registerClient,
  startCheckout,
  submitSignupRequest,
  type OnboardingPlan,
  type PaymentMode,
} from './onboardingApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SignupStep = 'plan' | 'details' | 'sent';

interface FormState {
  full_name: string;
  email: string;
  company_name: string;
  contact_no: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  full_name: '',
  email: '',
  company_name: '',
  contact_no: '',
  notes: '',
};

/** Human, technology-free billing period label from a plan's day count. */
function usePriceLabel() {
  const translate = useTranslate();
  return (plan: OnboardingPlan): string => {
    const amount = new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(plan.price) || 0);
    let period: string;
    if (plan.duration === 30) period = translate('onboarding.signup.per_month');
    else if (plan.duration === 365) period = translate('onboarding.signup.per_year');
    else period = translate('onboarding.signup.per_days', { count: plan.duration });
    return `${amount} ${period}`;
  };
}

const PlanCard: React.FC<{
  plan: OnboardingPlan;
  selected: boolean;
  priceLabel: string;
  onSelect: () => void;
}> = ({ plan, selected, priceLabel, onSelect }) => {
  const translate = useTranslate();
  const limit = (value?: number) =>
    value && value > 0 ? String(value) : translate('onboarding.signup.plan_features.unlimited');

  return (
    <Card
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{
        p: 2,
        cursor: 'pointer',
        borderRadius: 2,
        border: (theme) =>
          `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
        boxShadow: (theme) =>
          selected ? `0 0 0 3px ${theme.palette.primary.main}22` : 'none',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {plan.name}
          </Typography>
          {plan.tagline && (
            <Typography variant="body2" color="text.secondary">
              {plan.tagline}
            </Typography>
          )}
        </Box>
        {selected ? (
          <CheckCircleRounded color="primary" />
        ) : (
          <RadioButtonUncheckedRounded sx={{ color: 'action.disabled' }} />
        )}
      </Stack>
      <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
        {priceLabel}
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 1 }}>
        {plan.is_qr_type_static === 1 && (
          <Typography variant="caption" color="text.secondary">
            {translate('onboarding.signup.plan_features.static_qr', { count: limit(plan.static_qr_limit) })}
          </Typography>
        )}
        {plan.is_qr_type_dynamic === 1 && (
          <Typography variant="caption" color="text.secondary">
            {translate('onboarding.signup.plan_features.dynamic_qr', { count: limit(plan.dynamic_qr_limit) })}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {translate('onboarding.signup.plan_features.staff', { count: limit(plan.staff_member_limit) })}
        </Typography>
      </Stack>
    </Card>
  );
};

export const SignupPage: React.FC = () => {
  const translate = useTranslate();
  const priceLabel = usePriceLabel();

  const [step, setStep] = useState<SignupStep>('plan');
  const [plans, setPlans] = useState<OnboardingPlan[] | null>(null);
  const [mode, setMode] = useState<PaymentMode | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The plans and the mode are loaded as one thing on purpose: what the last
  // step does depends on the mode, so showing the funnel before it is known
  // would mean the visitor's button changes meaning while they are using it.
  const loadFunnel = () => {
    setPlans(null);
    setMode(null);
    setLoadError(false);
    Promise.all([fetchPublicPlans(), fetchPublicConfig()])
      .then(([rows, paymentMode]) => {
        setPlans(rows);
        setMode(paymentMode);
      })
      .catch(() => setLoadError(true));
  };

  useEffect(loadFunnel, []);

  const ready = plans !== null && mode !== null;
  const isManual = mode === 'manual';

  const setField = (key: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const emailValid = EMAIL_RE.test(form.email.trim());
  const detailsValid =
    form.full_name.trim() !== '' && emailValid && form.company_name.trim() !== '';

  const handleSubmit = async () => {
    setTouched(true);
    if (!detailsValid || selectedPlanId === null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isManual) {
        // No account, no identity, no session: only a request for a person.
        await submitSignupRequest({
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          company_name: form.company_name.trim(),
          contact_no: form.contact_no.trim() || undefined,
          plan_id: selectedPlanId,
          notes: form.notes.trim() || undefined,
        });
        setSubmitting(false);
        setStep('sent');
        return;
      }

      const account = await registerClient({
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        company_name: form.company_name.trim(),
        contact_no: form.contact_no.trim() || undefined,
        plan_id: selectedPlanId,
      });
      const payUrl = await startCheckout({ user_id: account.user_id, plan_id: account.plan_id });
      window.location.href = payUrl;
    } catch (error) {
      // Too many attempts from the same place: say what to do, not what happened.
      if (error instanceof HttpError && error.status === 429) {
        setSubmitError(translate('onboarding.signup.error_too_many'));
        setSubmitting(false);
        return;
      }
      // 4xx messages from the API are localized and user-safe; keys resolve too.
      const raw =
        error instanceof Error && error.message
          ? error.message
          : isManual
            ? 'onboarding.signup.error_request_generic'
            : 'onboarding.signup.error_generic';
      setSubmitError(raw.startsWith('onboarding.') ? translate(raw) : raw);
      setSubmitting(false);
    }
  };

  const activeStepIndex = step === 'plan' ? 0 : 1;

  return (
    <PublicShell maxWidth={step === 'plan' ? 560 : 480}>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        {translate(step === 'sent' ? 'onboarding.signup.sent_title' : 'onboarding.signup.title')}
      </Typography>
      {step !== 'sent' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('onboarding.signup.subtitle')}
        </Typography>
      )}

      {step !== 'sent' && (
        <Stepper activeStep={activeStepIndex} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>{translate('onboarding.signup.steps.plan')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>
              {translate(
                isManual ? 'onboarding.signup.steps.request' : 'onboarding.signup.steps.details'
              )}
            </StepLabel>
          </Step>
        </Stepper>
      )}

      {step !== 'sent' && loadError && (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {translate('onboarding.signup.error_plans')}
          </Alert>
          <Button variant="outlined" onClick={loadFunnel}>
            {translate('onboarding.signup.retry')}
          </Button>
        </Stack>
      )}

      {step !== 'sent' && !loadError && !ready && (
        <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            {translate('onboarding.signup.loading_plans')}
          </Typography>
        </Stack>
      )}

      {ready && step === 'plan' && (
        <PlanStep
          plans={plans}
          selectedPlanId={selectedPlanId}
          priceLabel={priceLabel}
          onSelect={setSelectedPlanId}
          onContinue={() => setStep('details')}
        />
      )}

      {ready && step === 'details' && (
        <Stack spacing={2}>
          <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
            {translate(
              isManual ? 'onboarding.signup.request_help' : 'onboarding.signup.details_help'
            )}
          </Alert>
          <TextField
            label={translate('onboarding.signup.fields.full_name')}
            value={form.full_name}
            onChange={setField('full_name')}
            required
            fullWidth
            error={touched && form.full_name.trim() === ''}
          />
          <TextField
            label={translate('onboarding.signup.fields.email')}
            type="email"
            value={form.email}
            onChange={setField('email')}
            required
            fullWidth
            error={touched && !emailValid}
          />
          <TextField
            label={translate('onboarding.signup.fields.company_name')}
            value={form.company_name}
            onChange={setField('company_name')}
            required
            fullWidth
            error={touched && form.company_name.trim() === ''}
          />
          <TextField
            label={translate('onboarding.signup.fields.contact_no')}
            value={form.contact_no}
            onChange={setField('contact_no')}
            fullWidth
          />
          {isManual && (
            <TextField
              label={translate('onboarding.signup.fields.notes')}
              value={form.notes}
              onChange={setField('notes')}
              helperText={translate('onboarding.signup.fields.notes_help')}
              multiline
              minRows={3}
              fullWidth
            />
          )}
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button onClick={() => setStep('plan')} disabled={submitting}>
              {translate('onboarding.signup.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !detailsValid}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {submitting
                ? translate('onboarding.signup.submitting')
                : translate(
                    isManual ? 'onboarding.signup.submit_request' : 'onboarding.signup.submit'
                  )}
            </Button>
          </Stack>
        </Stack>
      )}

      {step === 'sent' && (
        <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
          <MarkEmailReadOutlined color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="body1">{translate('onboarding.signup.sent_body')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('onboarding.signup.sent_help')}
          </Typography>
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary" align="center">
        {translate('onboarding.signup.have_account')}{' '}
        <Link component="button" type="button" onClick={goToSignIn} underline="hover">
          {translate('onboarding.signup.sign_in')}
        </Link>
      </Typography>
    </PublicShell>
  );
};

const PlanStep: React.FC<{
  plans: OnboardingPlan[];
  selectedPlanId: number | null;
  priceLabel: (plan: OnboardingPlan) => string;
  onSelect: (id: number) => void;
  onContinue: () => void;
}> = ({ plans, selectedPlanId, priceLabel, onSelect, onContinue }) => {
  const translate = useTranslate();

  const canContinue = useMemo(
    () => selectedPlanId !== null && plans.some((p) => p.plan_id === selectedPlanId),
    [selectedPlanId, plans]
  );

  if (plans.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        {translate('onboarding.signup.no_plans')}
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="subtitle2" gutterBottom>
        {translate('onboarding.signup.choose_plan')}
      </Typography>
      <Stack spacing={1.5}>
        {plans.map((plan) => (
          <PlanCard
            key={plan.plan_id}
            plan={plan}
            selected={selectedPlanId === plan.plan_id}
            priceLabel={priceLabel(plan)}
            onSelect={() => onSelect(plan.plan_id)}
          />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {translate('onboarding.signup.choose_plan_help')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" onClick={onContinue} disabled={!canContinue}>
          {translate('onboarding.signup.continue')}
        </Button>
      </Box>
    </>
  );
};

export default SignupPage;
